import { useState, useRef, useEffect } from 'react';
import {
  PlayIcon,
  EyeIcon,
  ClockIcon,
  CalendarIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import type { YouTubeVideo } from '@mermaids/shared';
import { formatViewCount, truncateTitle } from './utils';

interface YouTubeShortsCardProps {
  video: YouTubeVideo;
  index: number;
  onVideoClick: (videoId: string) => void;
}

const YouTubeShortsCard: React.FC<YouTubeShortsCardProps> = ({
  video,
  index,
  onVideoClick,
}) => {
  const [isInView, setIsInView] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = cardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className={`group relative bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-700 cursor-pointer overflow-hidden transform ${
        isInView ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
      } ${isHovered ? 'scale-105 rotate-1' : 'scale-100 rotate-0'}`}
      style={{
        transitionDelay: `${index * 0.15}s`,
        transformOrigin: 'center',
      }}
      onClick={() => onVideoClick(video.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

      {/* Shorts Badge */}
      <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center space-x-1 animate-pulse">
        <SparklesIcon className="w-3 h-3" />
        <span>SHORTS</span>
      </div>

      {/* Thumbnail Container - Portrait aspect ratio for Shorts */}
      <div className="relative overflow-hidden rounded-t-3xl">
        <div className="relative aspect-[9/16] max-h-80">
          <img
            src={
              video.thumbnail.high ||
              video.thumbnail.medium ||
              video.thumbnail.default
            }
            alt={video.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          {/* Play button overlay - larger for Shorts */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform group-hover:scale-100 scale-75">
            <div className="w-24 h-24 bg-gradient-to-r from-pink-600 to-purple-700 rounded-full flex items-center justify-center shadow-2xl animate-pulse hover:animate-none transform hover:scale-110 transition-all duration-300 border-4 border-white/20">
              <PlayIcon className="w-10 h-10 text-white ml-1" />
            </div>
          </div>

          {/* Duration badge */}
          <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-sm font-semibold flex items-center space-x-1 transform transition-all duration-300 group-hover:scale-110">
            <ClockIcon className="w-4 h-4" />
            <span>{video.duration}</span>
          </div>

          {/* View count badge */}
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1 rounded-lg text-sm font-semibold flex items-center space-x-1 transform transition-all duration-300 group-hover:scale-110">
            <EyeIcon className="w-4 h-4" />
            <span>{formatViewCount(video.viewCount)}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 relative z-10">
        <h3 className="font-heading font-bold text-lg text-gray-900 mb-3 group-hover:text-purple-600 transition-colors duration-300 line-clamp-2 leading-tight">
          {truncateTitle(video.title, 40)}
        </h3>

        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-4 h-4" />
            <span className="font-medium">
              {format(new Date(video.publishedAt), 'MMM dd')}
            </span>
          </div>

          <div className="text-right">
            <p className="font-semibold text-purple-600 text-xs">
              MERMAID SHORT
            </p>
          </div>
        </div>
      </div>

      {/* Animated border with gradient */}
      <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-gradient-to-r group-hover:from-pink-500 group-hover:via-purple-500 group-hover:to-blue-500 transition-all duration-500"></div>

      {/* Shimmer effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
        <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-pink-200/20 to-transparent animate-shimmer"></div>
      </div>

      {/* Floating sparkles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full animate-float-particles`}
            style={{
              left: `${15 + i * 12}%`,
              top: `${25 + i * 8}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${1.5 + i * 0.3}s`,
            }}
          ></div>
        ))}
      </div>
    </div>
  );
};

export default YouTubeShortsCard;
